import { FormEvent, useMemo, useState } from 'react';
import { declareRoute } from '@app/router/router';
import { useDocumentTitle } from '@app/hooks/useDocumentTitle';
import { trans } from '@app/translations';
import TextInput from '@app/components/UI/Form/TextInput';
import tw from 'twin.macro';
import { route } from '@app/router/generator';
import Button from '@app/components/UI/Button';
import Login from '@app/pages/Auth/Login';
import { useMutation } from '@app/api/apollo/useMutation';
import ResetPasswordMutation from '@graphql/mutation/user/ResetPassword.graphql';
import Alert from '@app/components/UI/Alert';
import Link from '@app/components/Router/Link';
import { useParams } from 'react-router-dom';
import { errorsByPath } from '@app/api/errors';
import { AppGraphQLError, GraphQLErrorCodes } from '@app/api/errors/GraphQLErrorCodes';
import { FormErrorsMap } from '@app/components/UI/Form';
import { FormRootErrors } from '@app/components/UI/Form/FormRootErrors';
import { MutationResult } from '@apollo/client';

interface MutationResponse {
  User: {
    resetPassword: boolean
  }
}

/**
 * After a user has requested a password reset, they are redirected to this page with a token.
 * The user can then enter a new password if the token is still valid.
 */
const ResetPassword = declareRoute(function Page() {
  useDocumentTitle(trans('pages.resetPassword.documentTitle'));

  const { token } = useParams();
  const [password, setPassword] = useState<string | null>(null);
  const [passwordConfirm, setPasswordConfirm] = useState<string | null>(null);
  const [resetPassword, resetPasswordState] = useMutation<MutationResponse>(ResetPasswordMutation);

  const hasSuccess = resetPasswordState.called && resetPasswordState.data?.User.resetPassword;

  function extractRootErrors({ error, data } : MutationResult<MutationResponse>) {
    const defaultErrorMessage = 'Une erreur est survenue.';

    if (data?.User?.resetPassword === false) {
      return defaultErrorMessage;
    }

    if (!error) {
      return undefined;
    }

    // if we have a custom error message, display it:
    if ((error.graphQLErrors as AppGraphQLError[])?.[0]?.code === GraphQLErrorCodes.CUSTOM_USER_ERROR) {
      return error.graphQLErrors[0]?.message ?? defaultErrorMessage;
    }

    return defaultErrorMessage;
  }

  const mappedErrors: FormErrorsMap<'newPassword' | 'newPasswordConfirm'> = useMemo(() => {
    const errors = resetPasswordState.error;

    return ({
      __root: extractRootErrors(resetPasswordState),
      ...(errors ? errorsByPath(errors.graphQLErrors as AppGraphQLError[]) : {}),
    });
  }, [resetPasswordState]);

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    resetPassword({
      variables: {
        token,
        payload: {
          newPassword: password,
          newPasswordConfirm: passwordConfirm,
        },
      },
    });
  }

  return <Container>
    <h2>{trans('pages.resetPassword.documentTitle')}</h2>

    {hasSuccess && <Alert variant="success">
      Votre mot de passe a été réinitialisé.

      Vous pouvez à présent <Link
        tw="text-success-800 hover:text-success-900 font-bold underline"
        to={route(Login)}
      >vous connecter</Link>.
    </Alert>}

    <FormRootErrors errors={mappedErrors.__root} />

    <form id="forgot-password" onSubmit={onSubmit}>

      <TextInput<string>
        id="new-password"
        label="Nouveau mot de passe"
        type="password"
        value={password}
        onChangedValue={setPassword}
        required
        disabled={hasSuccess}
        InputProps={{
          autoFocus: true,
          autoComplete: 'new-password',
        }}
        errors={mappedErrors.newPassword}
      />
      <TextInput<string>
        id="new-password-confirm"
        label="Confirmer le nouveau mot de passe"
        type="password"
        value={passwordConfirm}
        onChangedValue={setPasswordConfirm}
        required
        disabled={hasSuccess}
        InputProps={{
          autoComplete: 'new-password',
        }}
        errors={mappedErrors.newPasswordConfirm}
      />

      <FormActions>
        <Button href={route(Login)}>Retour à l&apos;identification</Button>
        <Button
          type="submit"
          variant="primary"
          disabled={resetPasswordState.loading || hasSuccess}
        >
          Changer le mot de passe
        </Button>
      </FormActions>
    </form>
  </Container>;
}, '/reset-password/:token');

const FormActions = tw.div`flex items-center justify-end mt-10`;
const Container = tw.div`max-w-[500px] w-full`;

export default ResetPassword;
